import { z } from 'zod';
import {
  languageSchema,
  messageCampaignPurposeSchema,
  messageCampaignSenderModeSchema,
  nameSchema,
  outboundChannelSchema,
  objectIdSchema,
  paginationSchema,
  type MessageCampaign,
  type OutboundChannel,
} from '@hero/schema';
import { API } from '..';
import { targetQueryAPISchema } from './targeting.api';

/**
 * Campaigns — a **target** (who, see `targeting.api.ts`) plus a
 * **message** (which template, on which of its channels). SuperAdmin.
 * The preview is `POST /targeting/resolve`; this file is the send and
 * the history. See docs/messaging/07-campaigns.md.
 */
export const messageCampaignCreateAPISchema = z.object({
  title: nameSchema.describe('What this campaign is about, for staff (e.g. "Riyadh carriers — Excel batch 1").'),
  description: z
    .string()
    .max(2000)
    .optional()
    .describe('Optional free text: why this campaign, who it is for.'),
  purpose: messageCampaignPurposeSchema
    .optional()
    .describe('Label only — `outreach`, `announcement` or `follow_up`. Not tied to a channel.'),
  templateId: z
    .string()
    .min(1)
    .describe('Catalog template id (`MESSAGE_CATALOG` key) — a `prospect` / `client` row.'),
  channels: z
    .array(outboundChannelSchema)
    .min(1)
    .describe(
      "Channels to send on — a subset of the template's catalog `channels`. WhatsApp needs a phone, push needs an account; a person is sent on every requested channel that can reach them.",
    ),
  target: targetQueryAPISchema.describe('Who — the targeting query.'),
  sender: z
    .object({
      mode: messageCampaignSenderModeSchema,
      channelAccountId: objectIdSchema
        .optional()
        .describe('`manual` only — the WhatsApp `ChannelAccount` to send from.'),
    })
    .optional()
    .describe(
      "WhatsApp line for the whole run. `auto` (default) = the template's pinned line, else the Hero line. `manual` must match the template's pin when it has one.",
    ),
  language: languageSchema
    .optional()
    .describe("Fix one language for the whole batch. Omit to render in each recipient's own."),
  variables: z
    .record(z.string())
    .optional()
    .describe(
      'Template variables shared by every recipient. Per-recipient ones (`name`) are filled server-side.',
    ),
});
export type MessageCampaignCreateAPI = API<
  z.infer<typeof messageCampaignCreateAPISchema>,
  MessageCampaign
>;

export const messageCampaignGetAllAPISchema = paginationSchema.extend({
  templateId: z.string().optional().describe('Filter history to one template.'),
  purpose: messageCampaignPurposeSchema.optional().describe('Filter history to one purpose.'),
  leadId: objectIdSchema.optional().describe('Only campaigns that reached this lead.'),
  userId: objectIdSchema.optional().describe('Only campaigns that reached this account.'),
  phone: z.string().optional().describe('Only campaigns that reached this phone (E.164).'),
  channelAccountId: objectIdSchema.optional().describe('Only campaigns sent from this WhatsApp line.'),
});
export type MessageCampaignGetAllAPI = API<
  z.infer<typeof messageCampaignGetAllAPISchema>,
  MessageCampaign[]
>;

export const messageCampaignGetOneAPISchema = z.object({
  _id: objectIdSchema,
});
export type MessageCampaignGetOneAPI = API<
  z.infer<typeof messageCampaignGetOneAPISchema>,
  MessageCampaign
>;

/**
 * `GET /message/campaigns/stats` — recompute the engagement snapshot
 * from the chat messages (at most every ~30s) and return the campaign
 * with `stats` and per-recipient `engagement` filled.
 */
export const messageCampaignStatsAPISchema = z.object({
  _id: objectIdSchema,
});
export type MessageCampaignStatsAPI = API<
  z.infer<typeof messageCampaignStatsAPISchema>,
  MessageCampaign
>;

/**
 * What a catalog template is, on every channel. On WhatsApp it is the
 * Meta category the Hub template is approved under.
 */
export const messageTemplateCategorySchema = z.enum(['marketing', 'utility', 'authentication']);
export type MessageTemplateCategory = z.infer<typeof messageTemplateCategorySchema>;

/**
 * How a template may be sent: `public` — the same copy for many people
 * (campaigns); `personalized` — about one person's record (an order, an
 * invoice, a grant), from product code or a staff chat. Some are both.
 */
export const messageTemplatePersonalizationSchema = z.enum(['personalized', 'public']);
export type MessageTemplatePersonalization = z.infer<typeof messageTemplatePersonalizationSchema>;

/**
 * `campaign` — `public` rows, on every channel they can go out on.
 * `chat` — what staff can send by hand on one WhatsApp thread.
 */
export const messageCampaignTemplatesAPISchema = z.object({
  scope: z.enum(['campaign', 'chat']).default('campaign'),
});

/** A catalog template a picker may offer (campaign composer / staff chat). */
export interface MessageCampaignTemplateOption {
  id: string;
  description: string;
  /** Channels the catalog row can go out on. */
  channels: OutboundChannel[];
  category: MessageTemplateCategory;
  personalization: MessageTemplatePersonalization[];
  /** Persona line the template is pinned to, if any. */
  line?: string;
  /** Variables the sender must supply (not filled per-recipient). */
  variables: string[];
  /** Variables the server fills per recipient (`name`). */
  perRecipientVariables: string[];
  /**
   * Our WhatsApp (or push) body copy for admin preview — same text the
   * transcript records. Hub may drift; this is what staff should expect.
   */
  message?: { en: string; ar: string; ur?: string };
}
export type MessageCampaignTemplatesAPI = API<
  z.input<typeof messageCampaignTemplatesAPISchema>,
  MessageCampaignTemplateOption[]
>;
